// noinspection JSUnresolvedReference

/*
 * findUserByEmail: This function will search a user by email address and returns the object.
 *
 * If the user is found, it will return the user's information.
 * If the user isn't found or an error occurs, it will return an error message.
 */
import makeRequest from "../api/apiRequest";
import { constructUrl } from "./apiConfig";
import apiClient from "./apiClient";
import { UserInfo, UserInfoSchema, AddToMfaPilotResponseSchema } from "../types/user";
import { validateApiResponse } from "../types/validators";

export const findUserByEmail = async (email: string): Promise<{
  userInfo: UserInfo | null;
  errorMsg: string | null;
}> => {
  const url = constructUrl("/user");
  const response = await makeRequest(url, "GET", null, { user: email }, {});

  if (response.errorType === 0) {
    // Request success and data exists
    try {
      const userInfo = validateApiResponse(UserInfoSchema, { errorType: 0, data: response.data }, 'findUserByEmail');
      return {
        userInfo,
        errorMsg: null,
      };
    } catch {
      return {
        userInfo: null,
        errorMsg: "User response failed validation",
      };
    }
  } else {
    console.log(
      `findUserByEmail: response.errorMessage= ${JSON.stringify(
        response.errorMessage
      )}`
    );
    return {
      userInfo: null,
      errorMsg: response.errorMessage,
    };
  }
};

export const findGIDByUsername = async (username: string): Promise<{
  userInfo: UserInfo | null;
  errorMsg: string | null;
}> => {
  const url = constructUrl("/gid");
  const response = await makeRequest(url, "GET", null, { user: username }, {});

  if (response.errorType === 0) {
    // Request success and data exists
    try {
      const userInfo = validateApiResponse(UserInfoSchema, { errorType: 0, data: response.data }, 'findGIDByUsername');
      return {
        userInfo,
        errorMsg: null,
      };
    } catch {
      return {
        userInfo: null,
        errorMsg: "GID response failed validation",
      };
    }
  } else {
    console.log(
      `findUserByUsername: response.errorMessage= ${JSON.stringify(
        response.errorMessage
      )}`
    );
    return {
      userInfo: null,
      errorMsg: response.errorMessage,
    };
  }
};

export const unlockAccount = async (userId: string): Promise<{
  errorMsg: string | null;
}> => {
  const endpoint = "/unlock_account";
  const url = constructUrl(endpoint);
  console.log("unlockAccount");
  const response = await makeRequest(url, "Post", { userid: userId }, {}, {});

  if (response.errorType === 0) {
    return {
      errorMsg: null,
    };
  } else {
    return { errorMsg: response.errorMessage };
  }
};

export const unlockGID = async (username: string): Promise<{
  errorMsg: string | null;
}> => {
  const endpoint = "/unlock_gid";
  const url = constructUrl(endpoint);
  console.log("unlockGID");
  const response = await makeRequest(url, "Post", { username: username }, {}, {});

  if (response.errorType === 0) {
    return {
      errorMsg: null,
    };
  } else {
    return { errorMsg: response.errorMessage };
  }
};

export const addToMfaPilot = async (userId: string): Promise<{
  data?: unknown;
  errorMsg: string | null;
}> => {
  const endpoint = "/add_to_mfa_pilot";
  const url = constructUrl(endpoint);
  console.log("addToMfaPilot");
  const response = await makeRequest(url, "Post", { userid: userId }, {}, {});

  if (response.errorType === 0) {
    try {
      const validatedData = validateApiResponse(
        AddToMfaPilotResponseSchema.shape.data,
        { errorType: 0, data: response.data },
        "addToMfaPilot"
      );
      return { data: validatedData, errorMsg: null };
    } catch {
      return { data: null, errorMsg: "Add-to-MFA-pilot response failed validation" };
    }
  } else {
    return { errorMsg: response.errorMessage };
  }
};

export const startOffNetworkEnrollment = async (userId: string): Promise<{
  data: unknown | null;
  errorMsg: string | null;
}> => {
  const url = import.meta.env.VITE_OFF_NETWORK_ENROLLMENT_URL || import.meta.env.REACT_APP_OFF_NETWORK_ENROLLMENT_URL;
  console.log("startOffNetworkEnrollment");

  try {
    const response = await apiClient.post(url, { user_id: userId });
    console.log("startOffNetworkEnrollment: success");
    return {
      data: response.data,
      errorMsg: null,
    };
  } catch (error: any) {
    console.log(`startOffNetworkEnrollment: error= ${error.message}`);
    const errorMsg = error.response?.data?.message || error.message || "Network error occurred";
    return {
      data: null,
      errorMsg: errorMsg,
    };
  }
};
